// Library imports
import moment from 'moment';
// Util imports
import { convertToISOString } from '../../utils/dates';
// Mock data
import ExampleTeamData from '../../utils/mock_data/ExampleTeamData';

// LineProphet's rule is "no prediction, or no projected scores". The PWP proxy strips projected
// scores from locked games, so it computes that server-side as predictionUnlock.hasPrediction.
export const gameMissingPrediction = game => !game?.prediction || game?.predictionUnlock?.hasPrediction === false;

// Same copy as LineProphet's game card warning banner.
export const getMissingPredictionMessage = sport =>
    sport === 'cfb'
        ? 'Predictions and data are only available for FBS teams that have played games this season.'
        : 'Model predictions are not available during the first week of the season.';

export const filterGames = (gameData, value, filters, activeDate) => {
    return gameData.filter(game => {
        // Check if game is on the active date
        if (activeDate !== 'all') {
            if (!moment(game.dateTime).isSame(convertToISOString(activeDate), 'D')) {
                return false;
            }
        }
        // Check if game passes at least 1 filter
        if (filters.length) {
            const passesFilter = filters.some(filter => {
                switch (filter) {
                    case 'road-dogs':
                        return game.awayTeam.odds?.fullGame?.spread > 0 && game.awayTeam.odds?.fullGame?.spread <= 3;
                    case 'road-favorites':
                        return game.awayTeam.odds?.fullGame.moneyline < 0;
                    case 'home-favs':
                        return game.homeTeam.odds?.fullGame.moneyline < -300 || game.homeTeam.odds?.fullGame?.spread < -10;
                    case 'division-game':
                        return (
                            game.awayTeam.conference === game.homeTeam.conference &&
                            game.awayTeam.division &&
                            game.homeTeam.division &&
                            game.awayTeam.division === game.homeTeam.division
                        );
                    case 'conference-game':
                        return game.awayTeam.conference === game.homeTeam.conference;
                    case 'Upcoming':
                        return game.status === 'not started';
                    case 'Live':
                        return game.status === 'live';
                    default:
                        if (game.advantages.find(adv => adv.id === filter)) {
                            return true;
                        } else {
                            return false;
                        }
                }
            });
            if (!passesFilter) return false;
        }
        // Check if game is included in the query (search) term
        if (!value) return true;
        const normalizedValue = value.toLowerCase();
        const matchesSearch = [
            game.awayTeam.teamName,
            game.homeTeam.teamName,
            game.awayTeam.abbr,
            game.homeTeam.abbr,
            game.awayTeam.teamName,
            game.homeTeam.teamName,
            game.gameId,
        ]
            .filter(Boolean)
            .some(name => name.toLowerCase().includes(normalizedValue));
        return matchesSearch;
    });
};

export const getGameTeamData = (teams, game, demo = false, sport) => {
    var teamsToUse = teams;
    if (demo) {
        teamsToUse = ExampleTeamData[sport];
    }
    const homeTeamData = teamsToUse.find(team => team.teamId === game.homeTeam.teamId);
    const awayTeamData = teamsToUse.find(team => team.teamId === game.awayTeam.teamId);
    return { homeTeamData, awayTeamData };
};

// Fill each game's team logo and color from the teams list so windows can render them without Redux.
export const attachTeamDetails = (games, teams) => {
    const findTeam = id => teams.find(team => String(team.teamId) === String(id));
    const withDetails = team => ({
        ...team,
        logoUrl: team?.logoUrl || findTeam(team?.teamId)?.logoUrl,
        primaryColor: team?.primaryColor || findTeam(team?.teamId)?.primaryColor,
    });
    return games.map(game => ({ ...game, awayTeam: withDetails(game.awayTeam), homeTeam: withDetails(game.homeTeam) }));
};

// e.g. "Oct 15, 2026"
export const formatShortDate = dateString =>
    new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

export const calcImpliedOdds = moneyline => {
    if (!moneyline || isNaN(moneyline)) return 0;
    var probability = 0;
    if (moneyline < 0) {
        probability = (-1 * moneyline) / (-1 * moneyline + 100);
    }
    if (moneyline > 0) {
        probability = 100 / (moneyline + 100);
    }
    return Number((probability * 100).toFixed(2));
};

// Convert confidence (0–1) to star rating (1–5)
export const getStarRating = confidence => {
    if (confidence == null) return 0;
    return Math.max(1, Math.round(confidence * 5));
};

/**
 * Returns the hex color only if it is light enough to read on a dark (#0f1117)
 * background. Dark colors (luminance < 0.38) are replaced with #e2e8f0.
 */
export const readableColor = (hex, fallback = '#e2e8f0') => {
    if (!hex || typeof hex !== 'string') return fallback;
    const s = hex.replace('#', '').trim();
    if (s.length !== 6) return fallback;
    const r = parseInt(s.slice(0, 2), 16);
    const g = parseInt(s.slice(2, 4), 16);
    const b = parseInt(s.slice(4, 6), 16);
    if ([r, g, b].some(Number.isNaN)) return fallback;
    return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.38 ? fallback : hex;
};
